import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, formatApiErrorDetail } from "@/auth";
import HomepageDateCard from "./HomepageDateCard";

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  date: today(),
  section1: { direct: "", house: "", ending: "" },
  section2: { direct: "", house: "", ending: "" },
  published: true,
});

function displayDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso || "";
}

export default function CommonNumberEditor() {
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadList = () =>
    axios
      .get(`${API}/admin/common-numbers`, { withCredentials: true })
      .then((r) => setRows(r.data || []))
      .catch(() => {});

  useEffect(() => {
    document.title = "Common Number | Admin";
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/common-numbers`, { withCredentials: true });
        setRows(r.data || []);
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const setField = (section, field, value) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      date: row.date,
      section1: row.section1,
      section2: row.section2,
      published: row.published ?? true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => { setEditingId(null); setForm(emptyForm()); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/common-numbers`, form, { withCredentials: true });
      toast.success(editingId ? "Common number updated" : "Common number saved");
      await loadList();
      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete common number for ${displayDate(row.date)}?`)) return;
    try {
      await axios.delete(`${API}/admin/common-numbers/${row.id}`, { withCredentials: true });
      toast.success("Deleted");
      if (editingId === row.id) reset();
      await loadList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="ap-card" data-testid="admin-common-number-page">Loading…</div>;

  return (
    <div data-testid="admin-common-number-page">
      <HomepageDateCard />
      <div className="ap-card">
        <h2 className="ap-card-title" data-testid="admin-cn-title">
          {editingId ? "EDIT COMMON NUMBER" : "UPDATE COMMON NUMBER"}
        </h2>
        <form onSubmit={submit} className="ap-form">
          <label className="ap-field">
            <span>Select Date:</span>
            <Input
              data-testid="cn-date-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              required
            />
          </label>

          <div className="ap-table">
            <div className="ap-table-head">
              <div>Direct</div>
              <div>House</div>
              <div>Ending</div>
            </div>
            <div className="ap-table-row">
              <Input data-testid="cn-s1-direct" value={form.section1.direct} onChange={(e) => setField("section1", "direct", e.target.value)} />
              <Input data-testid="cn-s1-house" value={form.section1.house} onChange={(e) => setField("section1", "house", e.target.value)} />
              <Input data-testid="cn-s1-ending" value={form.section1.ending} onChange={(e) => setField("section1", "ending", e.target.value)} />
            </div>
            <div className="ap-table-row">
              <Input data-testid="cn-s2-direct" value={form.section2.direct} onChange={(e) => setField("section2", "direct", e.target.value)} />
              <Input data-testid="cn-s2-house" value={form.section2.house} onChange={(e) => setField("section2", "house", e.target.value)} />
              <Input data-testid="cn-s2-ending" value={form.section2.ending} onChange={(e) => setField("section2", "ending", e.target.value)} />
            </div>
          </div>

          <div className="ap-form-inline">
            <button className="ap-btn ap-btn-primary" data-testid="cn-submit" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Submit"}
            </button>
            {editingId && (
              <button
                type="button"
                className="ap-btn ap-btn-secondary"
                data-testid="cn-cancel"
                onClick={reset}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {rows.length > 0 && (
        <div className="ap-card ap-card-list" data-testid="cn-list-card">
          <h2 className="ap-card-title ap-card-title--center">SAVED COMMON NUMBERS</h2>
          <div className="ap-table-scroll">
            <table className="ap-list" data-testid="cn-list">
              <thead>
                <tr>
                  <th>Sl.No</th>
                  <th>Date</th>
                  <th>Section 1 (D · H · E)</th>
                  <th>Section 2 (D · H · E)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id} data-testid={`cn-row-${row.id}`}>
                    <td>{i + 1}</td>
                    <td>{displayDate(row.date)}</td>
                    <td>{`${row.section1.direct || "--"} · ${row.section1.house || "--"} · ${row.section1.ending || "--"}`}</td>
                    <td>{`${row.section2.direct || "--"} · ${row.section2.house || "--"} · ${row.section2.ending || "--"}`}</td>
                    <td>
                      <button className="ap-icon-btn ap-icon-btn-edit" data-testid={`cn-edit-${row.id}`} onClick={() => startEdit(row)}>
                        <Pencil size={14} /> Edit
                      </button>
                      <button className="ap-icon-btn ap-icon-btn-delete" data-testid={`cn-delete-${row.id}`} onClick={() => remove(row)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
